import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, Code, ChevronDown, Trash2, AlertCircle } from 'lucide-react';
import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown';

// Variable Insertion Button Component
const VariableInsertionButton = ({ 
  onInsertVariable, 
  textareaRef, 
  availableVariables = [], 
  systemVariables = [],
  dynamicVariables = [], 
  onAddDynamicVariable, 
  onRemoveDynamicVariable
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewVariableModal, setShowNewVariableModal] = useState(false);
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableDescription, setNewVariableDescription] = useState('');
  const [validationError, setValidationError] = useState('');
  const [newVariableType, setNewVariableType] = useState('string');

  const dropdownRef = useRef(null);

  // Group system variables by category
  const groupedSystemVariables = systemVariables.reduce((groups, variable) => {
    const category = variable.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(variable);
    return groups;
  }, {});

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  const handleInsertVariable = (variableName) => {
    try {
      const variable = `{{${variableName}}}`;
      onInsertVariable(variable, textareaRef);
      setShowDropdown(false);
      console.log('Inserted variable:', variable);
    } catch (error) {
      console.error('Error inserting variable:', error);
    }
  };

  const validateVariableName = (name) => {
    if (!name.trim()) {
      return 'Variable name is required';
    }
    
    const cleanName = name.trim().toLowerCase().replace(/[^a-zA-Z0-9_]/g, '_');
    
    if (cleanName.length < 2) {
      return 'Variable name must be at least 2 characters long';
    }
    
    if (systemVariables.find(v => v.name === cleanName)) {
      return 'This variable name conflicts with a system variable';
    }
    
    if (dynamicVariables.find(v => v.name === cleanName)) {
      return 'This variable name already exists';
    }
    
    // Check if it starts with a number
    if (/^[0-9]/.test(cleanName)) {
      return 'Variable name cannot start with a number';
    }
    
    return null;
  };

  const handleAddNewVariable = () => {
    const error = validateVariableName(newVariableName);
    if (error) {
      setValidationError(error);
      return;
    }
    
    // Clean variable name
    const cleanName = newVariableName.trim().toLowerCase().replace(/[^a-zA-Z0-9_]/g, '_');
    
    const newVariable = {
      name: cleanName,
      description: newVariableDescription.trim() || `Custom variable: ${cleanName}`,
      category: 'Custom',
      variable_type: newVariableType,
      created: new Date().toISOString()
    };
    
    try {
      onAddDynamicVariable(newVariable);
      setNewVariableName('');
      setNewVariableDescription('');
      setNewVariableType('string');
      setValidationError('');
      setShowNewVariableModal(false);
      console.log('Added new dynamic variable:', newVariable);
    } catch (error) {
      console.error('Error adding dynamic variable:', error);
      setValidationError('Error adding variable. Please try again.');
    }
  };

  const handleRemoveVariable = (variableName, event) => {
    event.stopPropagation(); // Prevent dropdown from closing
    if (onRemoveDynamicVariable) {
      onRemoveDynamicVariable(variableName);
      console.log('Removed dynamic variable:', variableName);
    }
  };

  const allVariables = [...systemVariables, ...dynamicVariables];

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors focus:outline-none"
      >
        <Code className="w-3 h-3 mr-1" />
        Insert Variable
        <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showDropdown ? 'transform rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div 
          ref={dropdownRef}
          className="absolute z-20 mt-1 right-0 w-96 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto"
        >
          <div className="p-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">Available Variables</h4>
              <button
                onClick={() => setShowNewVariableModal(true)}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center bg-white px-2 py-1 rounded border border-indigo-200 hover:bg-indigo-50 transition-colors"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Custom
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Click any variable to insert it at your cursor position</p>
          </div>

          <div className="p-2 max-h-80 overflow-y-auto">
            {/* System Variables by Category */}
            {Object.entries(groupedSystemVariables).map(([category, variables]) => (
              <div key={category} className="mb-3">
                <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide px-2 py-1 bg-gray-50 rounded mb-1">
                  {category}
                </h5>
                {variables.map((variable) => (
                  <button
                    key={variable.name}
                    onClick={() => handleInsertVariable(variable.name)}
                    className="w-full text-left px-2 py-2 text-sm hover:bg-blue-50 rounded flex flex-col group transition-colors"
                  >
                    <span className="text-blue-600 group-hover:text-blue-700">{`{{${variable.name}}}`}</span>
                    <span className="text-xs text-gray-500 mt-0.5">{variable.description}</span>
                  </button>
                ))}
              </div>
            ))}

            {/* Dynamic Variables Section */}
            {dynamicVariables.length > 0 && (
              <div className="mb-3">
                <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide px-2 py-1 bg-purple-50 rounded mb-1">
                  Custom Variables
                </h5>
                {dynamicVariables.map((variable) => (
                  <div
                    key={variable.name}
                    className="group hover:bg-purple-50 rounded transition-colors"
                  >
                    <div className="flex items-center">
                      <button
                        onClick={() => handleInsertVariable(variable.name)}
                        className="flex-1 text-left px-2 py-2 text-sm"
                      >
                        <span className="text-purple-600 group-hover:text-purple-700">{`{{${variable.name}}}`}</span>
                        <span className="text-xs text-gray-500 block mt-0.5">{variable.description}</span>
                      </button>
                      {onRemoveDynamicVariable && (
                        <button
                          onClick={(e) => handleRemoveVariable(variable.name, e)}
                          className="p-1 mr-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove this variable"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {allVariables.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Code className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No variables available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Variable Modal */}
      {showNewVariableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 pb-2 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Add Custom Variable</h3>
              <button
                onClick={() => {
                  setShowNewVariableModal(false);
                  setValidationError('');
                  setNewVariableName('');
                  setNewVariableDescription('');
                }}
                className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Variable Name *
                </label>
                <input
                  type="text"
                  value={newVariableName}
                  onChange={(e) => {
                    setNewVariableName(e.target.value);
                    if (validationError) setValidationError('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="e.g., customer_tier, purchase_date"
                  maxLength={50}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use lowercase letters, numbers, and underscores only. Cannot start with a number.
                </p>
                {newVariableName && (
                  <p className="text-xs text-green-600 mt-1">
                    Will be saved as: <span className="">{`{{${newVariableName.trim().toLowerCase().replace(/[^a-zA-Z0-9_]/g, '_')}}}`}</span>
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={newVariableDescription}
                  onChange={(e) => setNewVariableDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Brief description of this variable"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Variable Type (optional)
                </label>
                <ModernTimeDropdown value={newVariableType} onChange={(e) => setNewVariableType(e.target.value)} options={['string', 'number']}/>
              </div>

              {validationError && (
                <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-red-800">{validationError}</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 p-4 pt-2 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => {
                  setShowNewVariableModal(false);
                  setValidationError('');
                  setNewVariableName('');
                  setNewVariableDescription('');
                  setNewVariableType('string');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNewVariable}
                disabled={!newVariableName.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Add Variable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Textarea Component with Variable Support
const TextareaWithVariables = ({ 
  label, 
  value = '', 
  onChange, 
  placeholder = '', 
  rows = 3,
  availableVariables = [],
  systemVariables = [],
  dynamicVariables = [],
  onAddDynamicVariable,
  onRemoveDynamicVariable,
  className = '',
  disabled = false,
  maxLength,
  headerActions,
  required = false
}) => {
  const textareaRef = useRef(null);

  const handleInsertVariable = (variable, ref) => {
    if (!ref.current || disabled) return;

    try {
      const textarea = ref.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = value || '';
      
      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
      
      // Create a synthetic event to maintain compatibility
      const syntheticEvent = {
        target: { value: newValue }
      };
      
      onChange(syntheticEvent);
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          const newPosition = start + variable.length;
          textarea.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
      
      console.log('Variable inserted successfully:', variable);
    } catch (error) {
      console.error('Error inserting variable:', error);
    }
  };

  // Function to extract used variables from the text
  const getUsedVariables = (text, systemVariables, dynamicVariables) => {
    if (!text) return { definedVariables: [], unlistedVariables: [] };
    
    // Extract all variables from the text using regex
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const matches = [...text.matchAll(variableRegex)];
    const usedVariableNames = matches.map(match => match[1].trim()).filter(Boolean);
    
    // Remove duplicates
    const uniqueUsedVariables = [...new Set(usedVariableNames)];
    
    // Separate into defined and unlisted variables
    const definedVariables = [];
    const unlistedVariables = [];
    
    uniqueUsedVariables.forEach(varName => {
      // First check system variables
      const systemVar = systemVariables.find(v => v.name === varName);
      if (systemVar) {
        definedVariables.push({ ...systemVar, type: 'system' });
        return;
      }
      
      // Then check dynamic variables
      const dynamicVar = dynamicVariables.find(v => v.name === varName);
      if (dynamicVar) {
        definedVariables.push({ ...dynamicVar, type: 'dynamic' });
        return;
      }
      
      // If not found in either, it's an unlisted variable
      unlistedVariables.push({ 
        name: varName, 
        description: 'Unlisted variable - click to add as custom', 
        type: 'unlisted' 
      });
    });
    
    return { definedVariables, unlistedVariables };
  };

  const handleAddUnlistedVariable = (variableName) => {
    if (onAddDynamicVariable) {
      const newVariable = {
        name: variableName,
        description: `Auto-added variable: ${variableName}`,
        category: 'Custom',
        variable_type: 'string',
        created: new Date().toISOString()
      };
      onAddDynamicVariable(newVariable);
      console.log('Auto-added unlisted variable:', newVariable);
    }
  };

  const { definedVariables, unlistedVariables } = getUsedVariables(value, systemVariables, dynamicVariables);
  const totalVariables = definedVariables.length + unlistedVariables.length;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className={`block text-sm font-medium text-gray-700 ${required ? "after:content-['*'] after:text-red-500 after:ml-1" : ''}`}>
          {label}
        </label>
        {!disabled && (
          <div className="flex flex-wrap items-center gap-2">
          {headerActions}
          <VariableInsertionButton
            onInsertVariable={handleInsertVariable}
            textareaRef={textareaRef}
            availableVariables={availableVariables}
            dynamicVariables={dynamicVariables}
            systemVariables={systemVariables}
            onAddDynamicVariable={onAddDynamicVariable}
            onRemoveDynamicVariable={onRemoveDynamicVariable}
          />
          </div>
        )}
      </div>
      
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          rows={rows}
          disabled={disabled}
          maxLength={maxLength}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm transition-colors ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''} ${className}`}
          placeholder={placeholder}
          style={{
            resize: 'vertical',
            minHeight: `${rows * 1.5}em`,
            paddingBottom: maxLength ? '2rem' : '0.5rem' // Add space for character count
          }}
        />
        
        {maxLength && (
          <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white bg-opacity-90 px-1.5 py-0.5 rounded border">
            <span className={`${(value || '').length >= maxLength * 0.9 ? 'text-orange-600' : (value || '').length >= maxLength ? 'text-red-600' : 'text-gray-400'}`}>
              {(value || '').length}/{maxLength}
            </span>
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        {/* Used Variables Section */}
        {totalVariables > 0 && (
          <div className="space-y-2">
            {/* Defined Variables */}
            {definedVariables.length > 0 && (
              <div>
                <span className="text-xs text-gray-600 font-medium">Used Variables: </span>
                <div className="inline-flex flex-wrap items-center gap-1">
                  {definedVariables.map((variable, index) => (
                    <span
                      key={`defined-${variable.name}-${index}`}
                      className={`inline-flex items-center px-2 py-1 text-xs rounded-md ${
                        variable.type === 'system' 
                          ? 'bg-gray-50 text-gray-700 border border-gray-200' 
                          : 'bg-purple-50 text-purple-700 border border-purple-200'
                      }`}
                      title={variable.description}
                      style={{ marginRight: '5px' }}
                    >
                      {`{{${variable.name}}}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Unlisted Variables */}
            {unlistedVariables.length > 0 && (
              <div>
                <span className="text-xs text-amber-700 font-medium">Unlisted Variables: </span>
                <div className="inline-flex flex-wrap items-center gap-1">
                  {unlistedVariables.map((variable, index) => (
                    <button
                      key={`unlisted-${variable.name}-${index}`}
                      onClick={() => handleAddUnlistedVariable(variable.name)}
                      className="inline-flex items-center px-2 py-1 text-xs rounded-md bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer"
                      title={`Click to add "${variable.name}" as a custom variable`}
                      style={{ marginRight: '5px' }}
                    >
                      {`{{${variable.name}}}`}
                      <Plus className="w-3 h-3 ml-1" />
                    </button>
                  ))}
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  Click unlisted variables to add them as custom variables
                </p>
              </div>
            )}
          </div>
        )}
        
        <div className="flex justify-between items-start">
          <p className="text-xs text-gray-500">
            Use variables like <code className="bg-gray-100 px-1 rounded text-xs">{`{{variable_name}}`}</code> to insert dynamic content
          </p>
          {value && value.includes('{{') && (
            <p className="text-xs text-green-600">
              ✓ Variables detected
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export { VariableInsertionButton, TextareaWithVariables };